import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  attendanceApi,
  scanningApi,
  tripsApi,
  vehicleRunsApi,
  shouldQueueOffline,
  type Stop,
  type Trip,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { CrewLayout } from '@/components/Layout';
import { Nav } from '@/components/Nav';
import { Button } from '@/components/Button';
import { CrewActionBar, CrewIcon, CrewMessage, CrewStatusChip } from '@/components/CrewUi';
import { OfflineIndicator } from '@/offline';
import {
  queueEvent,
  cacheCrewTrip,
  getCachedCrewTrip,
  cacheAttendanceStatus,
  getCachedAttendanceStatus,
  getQueuedEvents,
  OFFLINE_QUEUE_CHANGED,
  type QueuedEvent,
} from '@/offline/queue';
import { useConfirm, useToast } from '@/components/Feedback';
import { formatDateTime, stopTypeLabel } from '@/lib/format';
import { crewNavItems } from '@/pages/navItems';
import { resolveCrewNextTask, type CrewNextTask } from '@/lib/crewNextTask';
import { applyQueuedCrewActions, eventTypeFromNextTask } from '@/lib/crewStopAdvance';
import { newCrewActionMeta } from '@/lib/offlineReplay';
import { scanIsReconciled, scanNeedsAttention } from '@/lib/scanReconcile';
import { CARGO_COPY } from '@/lib/crewCargoCheck';
import {
  attendanceLocationHint,
  clockLocationNote,
  clockLocationPayload,
  readClockLocation,
} from '@/lib/clockLocation';
import { CrewCargoCheckPanel } from './CrewCargoCheckPanel';
import { CREW_QUEUED_MESSAGE, useCrewCargoCheck } from './useCrewCargoCheck';

const CREW_GUIDED_KEY = 'crew-guided';

const QUEUED_MESSAGE = CREW_QUEUED_MESSAGE;

type GeoResult = { pos: GeolocationPosition | null; denied: boolean };

function readGeolocation(): Promise<GeoResult> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ pos: null, denied: false });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ pos, denied: false }),
      (err) => resolve({ pos: null, denied: err.code === err.PERMISSION_DENIED }),
      // A fix from the last minute is fine and much faster on cheap phones.
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  });
}

function mapsHref(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

function guidedHref(task: CrewNextTask): string | null {
  if (!task.href || !task.stopId) return null;
  if (task.href === '/crew/scan' || task.href === '/crew/pod') {
    return `${task.href}?stopId=${encodeURIComponent(task.stopId)}&guided=1`;
  }
  return null;
}

export function CrewCurrentTrip() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const { data: trip, loading, error, reload } = useAsync(async () => {
    try {
      const current = await tripsApi.myCurrent();
      await cacheCrewTrip(current as unknown as Record<string, unknown> | null);
      return current;
    } catch (err) {
      const cached = await getCachedCrewTrip<Trip>();
      if (cached) return cached;
      throw err;
    }
  }, []);
  const { data: attendance, reload: reloadAttendance } = useAsync(async () => {
    try {
      const status = await attendanceApi.me();
      await cacheAttendanceStatus({
        clockedIn: status.clockedIn,
        hubGeofenceMatch: status.hubGeofenceMatch ?? null,
        hubDistanceM: status.hubDistanceM ?? null,
        locationStatus: status.locationStatus ?? null,
        recordedAt: status.recordedAt ?? null,
      });
      return status;
    } catch (err) {
      const cached = await getCachedAttendanceStatus();
      if (cached) return cached;
      throw err;
    }
  }, []);
  const [clockLoading, setClockLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  /** Gentle note after clocking in/out without a location (never blocks). */
  const [clockNote, setClockNote] = useState('');
  const [queuedActions, setQueuedActions] = useState<QueuedEvent[]>([]);
  const queuedRef = useRef<QueuedEvent[]>([]);
  const pendingCountRef = useRef<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [nextTask, setNextTask] = useState<CrewNextTask | null>(null);
  const autoNavDoneRef = useRef(false);

  const getGeolocation = useCallback(async (): Promise<GeolocationPosition | null> => {
    setLocating(true);
    try {
      return (await readGeolocation()).pos;
    } finally {
      setLocating(false);
    }
  }, []);

  // Actions saved on the phone while offline are applied on top of the last known
  // trip so the next step moves on (no repeat taps → no duplicate queued events).
  const refreshQueued = useCallback(async () => {
    try {
      const events = await getQueuedEvents();
      queuedRef.current = events;
      setQueuedActions(events);
      return events;
    } catch {
      return queuedRef.current;
    }
  }, []);

  useEffect(() => {
    let chain: Promise<void> = Promise.resolve();
    const handle = async () => {
      let events: QueuedEvent[];
      try {
        events = await getQueuedEvents();
      } catch {
        return;
      }
      const pending = events.filter((e) => e.status !== 'failed').length;
      const prev = pendingCountRef.current;
      pendingCountRef.current = pending;
      // Something was just sent (or discarded) — fetch the server's view *before* dropping
      // the overlay, so the next step never flips back to a done step (e.g. "Get signature"
      // right after a signature saved on the phone was uploaded).
      if (prev !== null && pending < prev && navigator.onLine) {
        await reload();
      }
      queuedRef.current = events;
      setQueuedActions(events);
    };
    // One at a time, in order, so a later queue change can't drop the overlay while an
    // earlier one is still fetching the trip.
    const onChange = () => {
      chain = chain.then(handle, handle);
    };
    onChange();
    window.addEventListener(OFFLINE_QUEUE_CHANGED, onChange);
    return () => window.removeEventListener(OFFLINE_QUEUE_CHANGED, onChange);
  }, [reload]);

  const viewTrip = useMemo(
    () => (trip ? applyQueuedCrewActions(trip, queuedActions) : trip),
    [trip, queuedActions],
  );

  const loadNextTrip = useCallback(async (tripId: string | undefined | null) => {
    if (!tripId) return null;
    try {
      const result = await vehicleRunsApi.nextTrip(tripId);
      return result.nextTrip ?? null;
    } catch {
      return null;
    }
  }, []);

  const computeNextTask = useCallback(async (rawTrip: Trip | null | undefined) => {
    const clockedIn = Boolean(attendance?.clockedIn);
    const currentTrip = rawTrip ? applyQueuedCrewActions(rawTrip, queuedRef.current) : rawTrip;
    if (!currentTrip) {
      const task = resolveCrewNextTask({ clockedIn, trip: null });
      setNextTask(task);
      return task;
    }

    const stops = (currentTrip.stops ?? []).map((s) => ({
      id: s.id,
      type: s.type,
      status: s.status,
      hasException: s.hasException,
      podId: s.podId,
      cargoCheck: s.cargoCheck ?? null,
    }));
    const currentStop = stops.find((s) => s.status !== 'departed');
    let scanReconciled = true;
    let scanNeedsReconcile = false;

    if (
      currentStop?.status === 'servicing' &&
      currentTrip.scanningEnabled &&
      !currentStop.hasException
    ) {
      try {
        const [counts, pending] = await Promise.all([
          scanningApi.counts(currentStop.id),
          scanningApi.pending(currentStop.id).catch(() => []),
        ]);
        const pendingCount = pending.length;
        scanNeedsReconcile = scanNeedsAttention(counts, pendingCount);
        scanReconciled = scanIsReconciled(counts, pendingCount, false);
      } catch {
        scanReconciled = false;
      }
    }

    const fetchedNext = await loadNextTrip(currentTrip.id);
    const nextTripNumber = fetchedNext?.tripNumber ?? null;

    const task = resolveCrewNextTask({
      clockedIn,
      trip: {
        id: currentTrip.id,
        status: currentTrip.status,
        scanningEnabled: currentTrip.scanningEnabled,
        rtbApprovalState: currentTrip.rtbApprovalState,
        stops,
      },
      scanReconciled,
      scanNeedsReconcile,
      nextTripId: fetchedNext?.id ?? null,
      nextTripNumber,
    });
    setNextTask(task);
    return task;
  }, [attendance?.clockedIn, loadNextTrip]);

  useEffect(() => {
    void computeNextTask(trip);
  }, [trip, queuedActions, attendance?.clockedIn, computeNextTask]);

  // Auto-navigate once to scan/pod when guided flag is set (post start_service / reload recovery).
  useEffect(() => {
    if (autoNavDoneRef.current || actionLoading || !nextTask) return;
    try {
      if (sessionStorage.getItem(CREW_GUIDED_KEY) !== 'true') return;
    } catch {
      return;
    }
    const href = guidedHref(nextTask);
    if (!href) return;
    autoNavDoneRef.current = true;
    navigate(href);
  }, [nextTask, actionLoading, navigate]);

  const markGuided = () => {
    try {
      sessionStorage.setItem(CREW_GUIDED_KEY, 'true');
    } catch {
      /* private mode */
    }
    autoNavDoneRef.current = false;
  };

  const handleClockIn = async () => {
    setClockLoading(true);
    setActionError('');
    setClockNote('');
    try {
      // Location is logged when available but never blocks clocking in.
      setLocating(true);
      const geo = await readClockLocation().finally(() => setLocating(false));
      const location = clockLocationPayload(geo);
      await attendanceApi.clockIn({ ...location, device: navigator.userAgent });
      await reloadAttendance();
      toast('Clocked in');
      setClockNote(clockLocationNote('in', location.locationStatus) ?? '');
    } catch (err) {
      setActionError(
        shouldQueueOffline(err)
          ? "Couldn't clock in: no signal. Move to a spot with signal, then tap Clock in again."
          : `Couldn't clock in: ${(err as { message?: string }).message ?? 'please try again.'}`,
      );
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    const tripOpen =
      !!viewTrip && viewTrip.status !== 'completed' && viewTrip.status !== 'cancelled';
    if (tripOpen) {
      const ok = await confirm({
        title: 'Clock out now?',
        message: `You still have a trip in progress (${viewTrip.reference}). Clock out anyway?`,
        confirmLabel: 'Clock out',
        cancelLabel: 'Stay clocked in',
      });
      if (!ok) return;
    }
    setClockLoading(true);
    setActionError('');
    setClockNote('');
    try {
      // Location is logged when available but never blocks clocking out.
      setLocating(true);
      const geo = await readClockLocation().finally(() => setLocating(false));
      const location = clockLocationPayload(geo);
      await attendanceApi.clockOut({ ...location, device: navigator.userAgent });
      await reloadAttendance();
      toast('Clocked out');
      setClockNote(clockLocationNote('out', location.locationStatus) ?? '');
    } catch (err) {
      setActionError(
        shouldQueueOffline(err)
          ? "Couldn't clock out: no signal. Move to a spot with signal, then tap Clock out again."
          : `Couldn't clock out: ${(err as { message?: string }).message ?? 'please try again.'}`,
      );
    } finally {
      setClockLoading(false);
    }
  };

  const handleStartTrip = async () => {
    if (!trip) return;
    setActionLoading(true);
    setActionError('');
    setActionMessage('');
    // One key + tap time for the online attempt and any offline replay of it.
    const action = newCrewActionMeta();
    try {
      const pos = await getGeolocation();
      const coords = pos
        ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMetres: pos.coords.accuracy }
        : undefined;
      await tripsApi.startTrip(trip.id, coords, action);
      await reload();
    } catch (err) {
      if (shouldQueueOffline(err)) {
        try {
          const pos = await getGeolocation();
          await queueEvent({
            type: 'crew_event',
            payload: {
              eventType: 'start_trip',
              tripId: trip.id,
              coords: pos
                ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMetres: pos.coords.accuracy }
                : undefined,
            },
            ...action,
          });
          await refreshQueued();
          setActionMessage(QUEUED_MESSAGE);
        } catch (queueErr) {
          setActionError((queueErr as Error).message ?? 'Could not queue start trip offline');
        }
      } else {
        const apiErr = err as { message?: string };
        setActionError(apiErr.message ?? 'Failed to start trip');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleNextAction = async () => {
    if (!trip || !nextTask) return;
    // Submit the event implied by the resolved CTA — never re-derive from stop.status
    // (RTB recovery statuses arrived/servicing/serviced must still send arrive_stop).
    const eventType = eventTypeFromNextTask(nextTask.kind);
    if (!eventType) return;

    const stopId =
      nextTask.stopId ??
      viewTrip?.stops?.find((s: Stop) => s.status !== 'departed')?.id;
    if (!stopId) return;

    setActionLoading(true);
    setActionError('');
    setActionMessage('');
    const action = newCrewActionMeta();
    try {
      const pos = await getGeolocation();
      await tripsApi.advanceStop(
        trip.id,
        stopId,
        eventType,
        pos ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMetres: pos.coords.accuracy } : undefined,
        action,
      );
      if (eventType === 'start_service') {
        markGuided();
      }
      await reload();
      const refreshed = await tripsApi.myCurrent().catch(() => null);
      if (refreshed) {
        const task = await computeNextTask(refreshed);
        const href = guidedHref(task);
        if (href && eventType === 'start_service') {
          autoNavDoneRef.current = true;
          navigate(href);
        }
      }
    } catch (err) {
      if (shouldQueueOffline(err)) {
        try {
          await queueEvent({
            type: 'stop_advance',
            payload: { tripId: trip.id, stopId, eventType },
            ...action,
          });
          await refreshQueued();
          setActionMessage(QUEUED_MESSAGE);
          if (eventType === 'start_service') markGuided();
        } catch (queueErr) {
          setActionError((queueErr as Error).message ?? 'Could not queue action offline');
        }
      } else {
        const apiErr = err as { message?: string };
        setActionError(apiErr.message ?? 'Action failed');
      }
    } finally {
      setActionLoading(false);
    }
  };

  /** "Confirm cargo" (no-manifest stop): piece count or PIC confirmation. Queued offline. */
  const cargo = useCrewCargoCheck({
    tripId: trip?.id,
    onSaved: async () => {
      // The panel closes: bring the stop card and the next step back into view.
      window.scrollTo({ top: 0 });
      await reload();
      const refreshed = await tripsApi.myCurrent().catch(() => null);
      if (refreshed) await computeNextTask(refreshed);
    },
    onQueued: refreshQueued,
    // e.g. 409 "The booking changed…" — refresh so the panel shows the current mode.
    onRejected: reload,
  });

  const handleEndTrip = async () => {
    if (!trip) return;
    setActionLoading(true);
    setActionError('');
    setActionMessage('');
    const action = newCrewActionMeta();
    try {
      const pos = await getGeolocation();
      const endedTripId = trip.id;
      await tripsApi.endTrip(
        endedTripId,
        pos ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMetres: pos.coords.accuracy } : undefined,
        action,
      );
      const fetchedNext = await loadNextTrip(endedTripId);
      await reload();
      const refreshed = await tripsApi.myCurrent().catch(() => null);
      if (refreshed) {
        await computeNextTask(refreshed);
      } else if (fetchedNext) {
        const nextNumber = fetchedNext.tripNumber;
        setNextTask({
          kind: 'continue_trip',
          label: `Continue to ${nextNumber}`,
          href: '/crew',
        });
      } else {
        await computeNextTask(null);
      }
    } catch (err) {
      if (shouldQueueOffline(err)) {
        try {
          const pos = await getGeolocation();
          await queueEvent({
            type: 'crew_event',
            payload: {
              eventType: 'end_trip',
              tripId: trip.id,
              coords: pos
                ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMetres: pos.coords.accuracy }
                : undefined,
            },
            ...action,
          });
          await refreshQueued();
          setActionMessage(QUEUED_MESSAGE);
        } catch (queueErr) {
          setActionError((queueErr as Error).message ?? 'Could not queue end trip offline');
        }
      } else {
        const apiErr = err as { message?: string };
        setActionError(apiErr.message ?? 'Failed to end trip');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleContinueTrip = async () => {
    if (!trip) {
      await reload();
      return;
    }
    if (trip.status === 'completed' || trip.status === 'cancelled') {
      await reload();
      return;
    }
    await handleEndTrip();
  };

  const handleRequestRtb = async () => {
    if (!trip) return;
    setActionLoading(true);
    setActionError('');
    try {
      await tripsApi.requestRtb(trip.id);
      await reload();
    } catch (err) {
      setActionError((err as { message?: string }).message ?? 'Could not ask dispatch for return-to-base approval. Try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrimaryCta = async () => {
    if (!nextTask || actionLoading) return;
    const href = guidedHref(nextTask);
    if (href) {
      markGuided();
      navigate(href);
      return;
    }

    switch (nextTask.kind) {
      case 'clock_in':
        await handleClockIn();
        break;
      case 'start_trip':
        await handleStartTrip();
        break;
      case 'arrive_stop':
      case 'start_service':
      case 'end_service':
      case 'depart_stop':
        await handleNextAction();
        break;
      case 'request_rtb':
        await handleRequestRtb();
        break;
      case 'continue_trip':
        await handleContinueTrip();
        break;
      case 'end_trip': {
        const ok = await confirm({
          title: "End this run?",
          message: "You won't be able to add stops or scans to this run. Dispatch can still send you more trips today.",
          confirmLabel: 'End run',
          cancelLabel: 'Not yet',
          danger: true,
        });
        if (ok) await handleEndTrip();
        break;
      }
      default:
        break;
    }
  };

  const tripNotStarted = viewTrip?.status === 'dispatched';
  // Location never gates the start — only being clocked in does.
  const canStartTrip = !!attendance?.clockedIn;
  const nextStop = viewTrip?.stops?.find((s: Stop) => s.status !== 'departed');
  const allStopsDeparted = !!viewTrip && !!viewTrip.stops?.length && !nextStop;
  // Routine finals auto-approve on the server; only block while explicitly pending.
  const rtbBlocking = viewTrip?.rtbApprovalState === 'pending';
  const primaryLabel = locating
    ? 'Getting location…'
    : nextTask
      ? `Next: ${nextTask.label}`
      : getNextActionLabel(nextStop);
  const busy = actionLoading || clockLoading;
  const primaryDisabled =
    actionLoading ||
    clockLoading ||
    (nextTask?.kind === 'start_trip' && !canStartTrip) ||
    (nextTask?.kind === 'end_trip' && rtbBlocking) ||
    (nextTask?.kind === 'idle') ||
    (nextTask?.kind === 'wait_rtb');

  const refreshAll = () => {
    setActionError('');
    void reload();
    void reloadAttendance();
  };

  const msgError = actionError || (!actionMessage ? cargo.error : '');
  const msgText = msgError || actionMessage || cargo.message;
  const actionBox = msgText ? (
    <CrewMessage kind={msgError ? 'error' : 'info'}>
      {msgText}
      {msgError.toLowerCase().includes('scan') && (
        <div className="crew-action-hint">
          Fix the scan problems, or <Link to="/crew/scan">file an exception</Link> on the Scan screen.
        </div>
      )}
    </CrewMessage>
  ) : null;

  const tripStops: Stop[] = viewTrip?.stops ?? [];
  const currentIndex = nextStop ? tripStops.findIndex((s) => s.id === nextStop.id) : -1;
  const otherStops = tripStops.filter((s) => s.id !== nextStop?.id);
  const showCargoPanel = nextTask?.kind === 'confirm_cargo' && !!nextStop?.cargoCheck;

  const primaryButton = (
    <Button
      variant="crew"
      size="crew"
      fullWidth
      type="button"
      className={nextTask?.kind === 'end_trip' ? 'crew-end-run-btn' : ''}
      loading={(actionLoading || (nextTask?.kind === 'clock_in' && clockLoading)) && !locating}
      disabled={primaryDisabled}
      onClick={() => void handlePrimaryCta()}
    >
      {primaryLabel}
    </Button>
  );

  return (
    <CrewLayout>
      <OfflineIndicator />
      <div className="crew-content">
        <Nav items={crewNavItems} variant="crew" />

        {viewTrip && actionBox}
        {viewTrip && tripNotStarted && !canStartTrip && (
          <CrewMessage kind="warn">Clock in before starting the trip.</CrewMessage>
        )}
        {viewTrip && !tripNotStarted && allStopsDeparted && viewTrip.rtbApprovalState === 'pending' && (
          <CrewMessage kind="warn">Waiting for dispatcher approval to return to base.</CrewMessage>
        )}

        <div className="crew-card crew-attendance">
          <div className="attendance-status">
            <span className={`attendance-dot ${attendance?.clockedIn ? 'in' : 'out'}`} />
            <span className="attendance-text">{attendance?.clockedIn ? 'Clocked in' : 'Not clocked in'}</span>
          </div>
          {!attendance?.clockedIn ? (
            <Button
              type="button"
              variant="secondary"
              className="crew-clock-btn"
              loading={clockLoading && !locating}
              disabled={clockLoading}
              onClick={() => void handleClockIn()}
            >
              {clockLoading && locating ? 'Getting location…' : 'Clock in'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              className="crew-clock-btn"
              loading={clockLoading && !locating}
              disabled={clockLoading}
              onClick={() => void handleClockOut()}
            >
              {clockLoading && locating ? 'Getting location…' : 'Clock out'}
            </Button>
          )}
          {clockNote ? (
            <div className="attendance-note" role="status">{clockNote}</div>
          ) : (
            attendance &&
            attendanceLocationHint(attendance) && (
              <div className="attendance-note">{attendanceLocationHint(attendance)}</div>
            )
          )}
        </div>

        {loading && !viewTrip ? (
          <div className="loading-center" style={{ padding: '3rem' }}><div className="spinner" /></div>
        ) : !viewTrip && error ? (
          <div className="crew-empty-card">
            <h2>Can&apos;t reach the server</h2>
            <p>Check your signal, then try again.</p>
            {actionBox}
            <CrewActionBar>
              <Button type="button" variant="crew" size="crew" fullWidth onClick={refreshAll}>
                Try again
              </Button>
            </CrewActionBar>
          </div>
        ) : !viewTrip ? (
          <div className="crew-empty-card">
            <h2>{nextTask?.kind === 'continue_trip' ? 'Trip complete' : 'No active trip'}</h2>
            <p>
              {nextTask?.kind === 'continue_trip'
                ? 'Continue to the next trip on this vehicle run.'
                : 'When dispatch assigns you a trip, it will appear here with your next step. Call dispatch if you expected one.'}
            </p>
            {actionBox}
            <Button type="button" variant="secondary" fullWidth onClick={refreshAll}>
              Refresh
            </Button>
            {(nextTask?.kind === 'clock_in' || nextTask?.kind === 'continue_trip') && (
              <CrewActionBar>
                <Button
                  type="button"
                  variant="crew"
                  size="crew"
                  fullWidth
                  loading={busy && !locating}
                  disabled={busy || (primaryDisabled && nextTask.kind !== 'continue_trip')}
                  onClick={() => void handlePrimaryCta()}
                >
                  {primaryLabel}
                </Button>
              </CrewActionBar>
            )}
          </div>
        ) : (
          <div className="crew-trip">
            <div className="crew-trip-header">
              <span className="crew-label">Trip</span>
              <CrewStatusChip status={viewTrip.status} />
            </div>
            <h1 className="crew-ref">{viewTrip.reference}</h1>

            {showCargoPanel && nextStop?.cargoCheck && (
              <CrewCargoCheckPanel
                key={nextStop.id}
                stopType={nextStop.type}
                cargoCheck={nextStop.cargoCheck}
                defaultPicName={nextStop.contactName}
                busy={cargo.busy}
                onSubmit={(body) => cargo.submit(nextStop.id, body)}
              />
            )}

            {nextStop && (
              <section className="crew-card crew-card-hl crew-current-stop" aria-label="Current stop">
                <div className="crew-stop-head">
                  <span className="crew-stop-num current">{nextStop.sequence}</span>
                  <div className="crew-stop-head-text">
                    <span className="crew-kicker">Stop {currentIndex + 1} of {tripStops.length}</span>
                    <span className="crew-stop-type">{stopTypeLabel(nextStop.type)}</span>
                  </div>
                  <CrewStatusChip status={nextStop.status} />
                </div>
                <p className="crew-current-addr">{nextStop.address}</p>
                {((nextStop.address && nextStop.address !== 'Address not set') || nextStop.contactPhone) && (
                  <div className="crew-stop-links">
                    {nextStop.address && nextStop.address !== 'Address not set' && (
                      <a className="crew-stop-link" href={mapsHref(nextStop.address)} target="_blank" rel="noopener noreferrer">
                        <CrewIcon name="navigate" />
                        <span>Navigate</span>
                      </a>
                    )}
                    {nextStop.contactPhone && (
                      <a className="crew-stop-link" href={`tel:${nextStop.contactPhone.replace(/[^\d+]/g, '')}`}>
                        <CrewIcon name="call" />
                        <span>Call {nextStop.contactName ? nextStop.contactName.split(' ')[0] : 'contact'}</span>
                      </a>
                    )}
                  </div>
                )}
                {(nextStop.scheduledTime || nextStop.contactName) && (
                  <div className="crew-stop-meta">
                    {nextStop.scheduledTime && (
                      <div><CrewIcon name="clock" className="crew-meta-icon" />Due {formatDateTime(nextStop.scheduledTime)}</div>
                    )}
                    {nextStop.contactName && (
                      <div><CrewIcon name="person" className="crew-meta-icon" />Contact: {nextStop.contactName}</div>
                    )}
                  </div>
                )}
                {nextStop.hasException && <span className="crew-chip crew-chip-amber crew-exception-tag">Exception filed</span>}
                {nextStop.cargoCheck?.completed && <CargoSummaryLine stop={nextStop} />}
              </section>
            )}

            {viewTrip.cargoSummary && (
              <div className="crew-card">
                <span className="crew-label">Cargo</span>
                <span>{viewTrip.cargoSummary}</span>
              </div>
            )}

            {otherStops.length > 0 && (
              <div className="crew-stops">
                <span className="crew-section-label">{nextStop ? 'Other stops' : 'Stops'}</span>
                {otherStops.map((stop: Stop) => (
                  <div key={stop.id} className="crew-card crew-stop-row">
                    <span className="crew-stop-num">{stop.sequence}</span>
                    <div className="crew-stop-body">
                      <div className="crew-stop-row-top">
                        <span className="crew-stop-type">{stopTypeLabel(stop.type)}</span>
                        <CrewStatusChip status={stop.status} />
                      </div>
                      <div className="crew-stop-addr">{stop.address}</div>
                      {stop.hasException && <span className="crew-exception-text">Exception filed</span>}
                      {stop.cargoCheck?.completed && <CargoSummaryLine stop={stop} />}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!showCargoPanel && <CrewActionBar>{primaryButton}</CrewActionBar>}
          </div>
        )}
      </div>

      <style>{`
        .crew-attendance {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: var(--cs-3);
          margin-bottom: var(--crew-section);
        }
        .attendance-status {
          display: flex;
          align-items: center;
          gap: var(--cs-3);
          min-width: 0;
          flex: 1 1 auto;
        }
        .attendance-text { font-size: var(--crew-fs-card); font-weight: 600; }
        .attendance-dot { width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0; }
        .attendance-dot.in { background: var(--crew-ok-bright); }
        .attendance-dot.out { background: var(--crew-disabled); }
        .attendance-note { flex: 1 1 100%; color: var(--crew-muted); font-size: var(--crew-fs-support); }
        .crew-shell .crew-clock-btn { min-width: 120px; flex-shrink: 0; }
        .crew-trip-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--cs-3);
        }
        .crew-trip-header .crew-label { margin: 0; }
        .crew-shell .crew-ref {
          font-size: var(--crew-fs-title);
          font-weight: 700;
          line-height: 1.2;
          margin: var(--cs-1) 0 var(--crew-gap);
          overflow-wrap: anywhere;
        }
        .crew-stop-head { display: flex; align-items: flex-start; gap: var(--cs-3); }
        .crew-stop-head-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
        .crew-stop-type { font-weight: 600; font-size: var(--crew-fs-card); }
        .crew-stop-num {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 2px solid var(--crew-muted);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
        }
        .crew-stop-num.current { width: 44px; height: 44px; background: var(--crew-signal); color: var(--crew-bg); border-color: var(--crew-signal); font-size: 1.125rem; }
        .crew-current-addr { font-size: 1.25rem; font-weight: 600; line-height: 1.3; margin: var(--cs-3) 0; overflow-wrap: anywhere; }
        .crew-stop-links { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--cs-3); margin-bottom: var(--cs-3); }
        .crew-stop-links > :only-child { grid-column: 1 / -1; }
        .crew-stop-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--cs-2);
          min-width: 0;
          min-height: var(--crew-btn);
          padding: 0 var(--cs-3);
          border-radius: var(--crew-r);
          border: 2px solid var(--crew-muted);
          color: var(--crew-white);
          font-weight: 700;
          font-size: var(--crew-fs-body);
          text-decoration: none;
        }
        .crew-stop-link:hover { text-decoration: none; background: rgba(255,255,255,0.06); }
        .crew-stop-link span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .crew-stop-meta { display: flex; flex-direction: column; gap: var(--cs-1); color: var(--crew-white); font-size: var(--crew-fs-body); }
        .crew-stop-meta > div { display: flex; align-items: flex-start; gap: var(--cs-2); overflow-wrap: anywhere; }
        .crew-meta-icon { width: 22px; height: 22px; flex-shrink: 0; color: var(--crew-muted); margin-top: 1px; }
        .crew-exception-tag { margin-top: var(--cs-3); }
        .crew-exception-text { display: block; color: var(--crew-amber); font-weight: 600; font-size: var(--crew-fs-support); }
        .crew-cargo-line { margin-top: var(--cs-2); color: var(--crew-ok-bright); font-weight: 600; font-size: var(--crew-fs-support); }
        .crew-stop-row { display: flex; gap: var(--cs-3); align-items: flex-start; }
        .crew-stop-body { min-width: 0; flex: 1; }
        .crew-stop-row-top { display: flex; align-items: center; justify-content: space-between; gap: var(--cs-2); }
        .crew-stop-row .crew-stop-type { font-size: var(--crew-fs-body); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .crew-stop-addr {
          color: var(--crew-muted);
          font-size: var(--crew-fs-support);
          margin-top: 2px;
          overflow-wrap: anywhere;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .crew-shell .crew-end-run-btn { outline: 3px solid var(--crew-amber); outline-offset: 2px; }
        .crew-action-hint { margin-top: var(--cs-1); font-size: var(--crew-fs-support); }
        .crew-empty-card .btn-secondary { margin-top: var(--cs-2); }
      `}</style>
    </CrewLayout>
  );
}

function CargoSummaryLine({ stop }: { stop: Stop }) {
  const check = stop.cargoCheck;
  if (!check?.completed) return null;
  return (
    <div className="crew-cargo-line">
      {CARGO_COPY.confirmedSummary}
      {check.countedPieces != null ? ` · ${check.countedPieces} pcs` : ''}
      {check.picName ? ` · PIC ${check.picName}` : ''}
    </div>
  );
}

function getNextActionLabel(stop: Stop | undefined): string {
  if (!stop) return 'Complete';
  switch (stop.status) {
    case 'pending': return 'Mark arrived';
    case 'arrived': return 'Start servicing';
    case 'servicing': return 'Mark serviced';
    case 'serviced': return 'Depart stop';
    default: return 'Next action';
  }
}
